/**
 * EmailDuplicateValidator - メールアドレス重複チェック付き入力コンポーネント
 * メールアドレス入力時にリアルタイムで重複チェックを行い、警告を表示する
 * FL-52: 全システムでメールアドレス重複防止機能
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { CompanyUserService } from '../../services/companyUserService';
import { FormInput } from './common/FormInput';
import logger from '../../utils/logger';

interface EmailDuplicateValidatorProps {
  /** 現在のメールアドレス値 */
  value: string;
  /** 値変更時のコールバック */
  onChange: (value: string) => void;
  /** 除外するユーザーID（更新時に自分自身を除外） */
  excludeUserId?: string;
  /** ラベル */
  label?: string;
  /** プレースホルダー */
  placeholder?: string;
  /** 必須フィールドかどうか */
  required?: boolean;
  /** 無効化フラグ */
  disabled?: boolean;
  /** 重複検出時のコールバック（保存ボタン制御用） */
  onDuplicateStatusChange?: (isDuplicate: boolean) => void;
  /** メール形式妥当性変更時のコールバック */
  onFormatValidityChange?: (isValid: boolean) => void;
}

// Removed ExistingUser interface - no longer displaying detailed information

export const EmailDuplicateValidator: React.FC<EmailDuplicateValidatorProps> = ({
  value,
  onChange,
  excludeUserId,
  label = 'メールアドレス',
  placeholder = 'メールアドレスを入力',
  required = false,
  disabled = false,
  onDuplicateStatusChange,
  onFormatValidityChange,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [lastCheckedValue, setLastCheckedValue] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [hasCheckedOnce, setHasCheckedOnce] = useState(false);
  const [touched, setTouched] = useState(false);
  const [formatError, setFormatError] = useState('');

  const companyUserService = useMemo(() => new CompanyUserService(), []);

  // デバウンスタイマー管理用のRef
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // メール形式チェックヘルパー（より厳格なバリデーション）
  const isValidEmailFormat = useCallback((email: string) => {
    if (!email || !email.trim()) return false;

    // より厳格な正規表現: 少なくとも2文字以上のTLDが必要
    const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
    const isBasicFormatValid = emailRegex.test(email);

    // TLD部分の検証を強化
    const parts = email.split('.');
    const tld = parts[parts.length - 1];

    // TLD部分が2文字以上で文字のみであることを確認
    const isTldValid = Boolean(tld && tld.length >= 2 && /^[a-zA-Z]+$/.test(tld));

    return isBasicFormatValid && isTldValid;
  }, []);

  // 状態をクリアするヘルパー
  const clearValidationState = useCallback(() => {
    setIsDuplicate(false);
    setError(null);
    setIsChecking(false);
    setHasCheckedOnce(false);
    // lastCheckedValueはクリアしない（値変更判定に必要）
    onDuplicateStatusChange?.(false);
  }, [onDuplicateStatusChange]);

  // メールアドレス重複チェック
  const checkEmailDuplicate = useCallback(
    async (
      email: string,
      currentInputValue: string,
      currentLastCheckedValue: string,
      currentIsChecking: boolean,
      currentIsDuplicate: boolean
    ) => {
      // 空の場合やメール形式でない場合はチェックしない
      if (!isValidEmailFormat(email)) {
        clearValidationState();
        return;
      }

      // 既にチェック済みの値で重複状態が確定している場合のみスキップ
      // isDuplicateがfalseの場合は再チェックが必要
      if (email === currentLastCheckedValue && !currentIsChecking && currentIsDuplicate) {
        return;
      }

      setIsChecking(true);
      setError(null);

      try {
        const result = await companyUserService.checkEmailDuplicate(email, excludeUserId);

        // チェック完了後、まだ同じ値かどうか確認（非同期処理中に値が変わった可能性）
        if (email === currentInputValue) {
          setIsDuplicate(result.isDuplicate);
          onDuplicateStatusChange?.(result.isDuplicate);
          setLastCheckedValue(email);
          setIsTyping(false); // APIチェック完了時に入力中フラグをリセット
          setHasCheckedOnce(true); // チェック完了フラグを設定
        }
      } catch (err) {
        logger.error('メールアドレス重複チェックエラー:', err);
        if (email === currentInputValue) {
          // エラー時は重複チェック結果をリセット（利用可能として扱う）
          setError('重複チェックでエラーが発生しました。再度お試しください。');
          setIsDuplicate(false);
          onDuplicateStatusChange?.(false);
          setLastCheckedValue(''); // エラー時はチェック済み状態をリセット
          setIsTyping(false); // エラー時も入力中フラグをリセット
          setHasCheckedOnce(true); // エラー時もチェック試行済みとして記録
        }
      } finally {
        if (email === currentInputValue) {
          setIsChecking(false);
        }
      }
    },
    [
      companyUserService,
      excludeUserId,
      onDuplicateStatusChange,
      isValidEmailFormat,
      clearValidationState,
    ]
  );

  // コンポーネントマウント時に初期値の妥当性を通知
  useEffect(() => {
    const isValid = isValidEmailFormat(value);
    onFormatValidityChange?.(isValid);
  }, [value, onFormatValidityChange]); // useCallbackでメモ化された関数は除外

  // 外部から渡されるvalueの変更を監視してinputValueを同期
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value);
    }
  }, [value, inputValue]);

  // inputValueの変更時にデバウンス後にAPIチェックを実行
  useEffect(() => {
    // 前のタイマーを必ずクリア
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // メール形式の妥当性を通知
    const isValidFormat = isValidEmailFormat(inputValue);
    onFormatValidityChange?.(isValidFormat);

    // 無効な形式の場合は状態をクリア（lastCheckedValueもクリア）
    if (!isValidFormat) {
      setIsDuplicate(false);
      setError(null);
      setIsChecking(false);
      setHasCheckedOnce(false);
      setLastCheckedValue(''); // 無効な形式の場合は最後にチェックした値もクリア
      onDuplicateStatusChange?.(false);
      return;
    }

    // デバウンス付きでAPIチェックを実行
    debounceTimerRef.current = setTimeout(() => {
      checkEmailDuplicate(inputValue, inputValue, lastCheckedValue, isChecking, isDuplicate);
    }, 300); // 300msのデバウンス

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [inputValue, isValidEmailFormat, onDuplicateStatusChange, onFormatValidityChange]);

  // コンポーネントアンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, []);

  const handleInputChange = (_name: string, newValue: string) => {
    onChange(newValue);
    setInputValue(newValue);

    // メール形式の妥当性を親コンポーネントに通知
    const isValidFormat = isValidEmailFormat(newValue);
    onFormatValidityChange?.(isValidFormat);

    // 値が変更された場合、入力中フラグを設定
    if (newValue !== lastCheckedValue) {
      setIsTyping(true);
      setError(null); // エラーはクリア
      setFormatError(''); // フォーマットエラーもクリア
    }

    // 無効な形式の場合は即座に全ての状態をクリア
    if (!isValidFormat) {
      setIsDuplicate(false);
      setError(null);
      setIsChecking(false);
      setIsTyping(false);
      setHasCheckedOnce(false);
      setLastCheckedValue(''); // 無効な形式の場合は最後にチェックした値もクリア
      onDuplicateStatusChange?.(false);
    } else if (newValue !== lastCheckedValue) {
      // 有効な形式で未チェックの値の場合は、チェック中表示を開始
      if (!isChecking) {
        setIsChecking(true);
      }
    }
  };

  const handleBlur = () => {
    setTouched(true);
    // 空文字列や空白のみの場合をより確実にチェック
    if (!inputValue || !inputValue.trim()) {
      setFormatError('メールアドレスは必須です');
    } else if (!isValidEmailFormat(inputValue)) {
      setFormatError('有効なメールアドレスを入力してください');
    } else {
      setFormatError('');
    }
  };

  // 検証状態の表示内容を決定
  const getValidationDisplay = () => {
    // フォーマットエラーを最優先で表示（フォーカスが外れた時）
    if (touched && formatError) {
      return (
        <div className='flex items-center mt-1 text-sm text-red-600 dark:text-red-400'>
          <ExclamationTriangleIcon className='h-4 w-4 mr-1' />
          {formatError}
        </div>
      );
    }

    if (isChecking) {
      return (
        <div className='flex items-center mt-1 text-sm text-blue-600 dark:text-blue-400'>
          <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2'></div>
          確認中...
        </div>
      );
    }

    if (error) {
      return (
        <div className='flex items-center mt-1 text-sm text-red-600 dark:text-red-400'>
          <ExclamationTriangleIcon className='h-4 w-4 mr-1' />
          {error}
        </div>
      );
    }

    // 入力中は重複警告を一時的に非表示（1文字削除時の即座反応のため）
    if (isDuplicate && !isTyping) {
      return (
        <div className='flex items-center mt-1 text-sm text-red-600 dark:text-red-400'>
          <ExclamationTriangleIcon className='h-4 w-4 mr-1' />
          このメールアドレスは既に登録されています
        </div>
      );
    }

    if (inputValue && hasCheckedOnce && !isDuplicate && !isChecking && !isTyping) {
      // メール形式が正しく、実際にチェック完了して重複していない場合のみ成功表示
      if (isValidEmailFormat(inputValue)) {
        return (
          <div className='flex items-center mt-1 text-sm text-green-600 dark:text-green-400'>
            <CheckCircleIcon className='h-4 w-4 mr-1' />
            利用可能なメールアドレスです
          </div>
        );
      }
    }

    return null;
  };

  return (
    <div className='space-y-1'>
      <FormInput
        name='email'
        label={label}
        type='email'
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        debounceMs={0} // デバウンスは内部で管理するため無効化
        autoComplete='email'
      />
      {getValidationDisplay()}
    </div>
  );
};
